import { useEffect, useRef } from "react";
import { stillWanted } from "./Kit";

/**
 * 小爱 as the prototype draws her (v2.20 core.js AiOrb), the same shader to the number: a teal–violet fluid sphere that
 * breathes, its glows drifting; busy (a new customer, her replying) the flow runs faster and a ring pulses out; rose at
 * the heart when she cannot answer (the prototype's sad mood). It was CSS gradients for a while (round 3), which the
 * owner saw at once beside the prototype (10/25: 「球和侧栏的球」).
 *
 * One hidden WebGL canvas draws every orb on the page and copies into each orb's own 2D canvas, so any number of orbs
 * costs one GL context. The shader compiles off the page's thread where the browser can (KHR_parallel_shader_compile);
 * until it is ready, or with no WebGL, the orb is the same sphere from radial gradients. An orb off screen, or on a
 * page that is hidden, is not drawn, and with none to draw the loop stops; a hidden tab stops it too. Asked for less
 * motion, each orb is drawn once and stays still.
 *
 * Unlike the prototype, the GL canvas grows to the biggest orb and each one is drawn in its corner, rather than the
 * canvas being resized for every orb every frame (the sidebar's 22 and the hero's 72 side by side).
 */

interface Item {
  ctx: CanvasRenderingContext2D;
  px: number;
  /** 0 calm, 1 busy, eased toward what it should be. */
  b: number;
  busy: boolean;
  /** 1: rose at the heart. */
  lo: number;
  seed: number;
  vis: boolean;
}

interface Loc {
  t: WebGLUniformLocation | null;
  b: WebGLUniformLocation | null;
  lo: WebGLUniformLocation | null;
}

const ORB: {
  items: Set<Item>;
  gl: WebGLRenderingContext | null;
  cv: HTMLCanvasElement | null;
  pr: WebGLProgram | null;
  ext: { COMPLETION_STATUS_KHR: number } | null;
  loc: Loc | null;
  raf: number;
  ok: boolean | "wait" | null;
  busyUntil: number;
  t0: number;
} = { items: new Set(), gl: null, cv: null, pr: null, ext: null, loc: null, raf: 0, ok: null, busyUntil: 0, t0: performance.now() };

const VS = "attribute vec2 p;varying vec2 v;void main(){v=p*.5+.5;gl_Position=vec4(p,0.,1.);}";
const FS = `precision mediump float;varying vec2 v;uniform float t,b,lo;
void main(){
  vec2 p=v*2.-1.;float r=length(p);
  float tt=t*(.38+b*1.25);
  float R=.78+.02*sin(t*1.5)+.02*b*sin(t*7.);
  vec2 q=p/R;float d2=dot(q,q);float z=sqrt(max(0.,1.-d2));
  vec3 teal=vec3(.05,.74,.66),mint=vec3(.50,1.,.86),vio=vec3(.55,.28,1.),blue=vec3(.22,.55,1.),rose=vec3(1.,.35,.55);
  vec3 c0=mix(teal,rose,lo*.7);
  vec2 a1=vec2(cos(tt*.9),sin(tt*1.1))*.42,a2=vec2(cos(tt*1.27+2.1),sin(tt*.83+1.3))*.48,a3=vec2(cos(tt*.71+4.2),sin(tt*1.19+3.7))*.38;
  float g1=exp(-2.6*dot(q-a1,q-a1)),g2=exp(-2.9*dot(q-a2,q-a2)),g3=exp(-3.8*dot(q-a3,q-a3));
  vec3 col=mix(vec3(.04,.30,.34),vec3(.20,.10,.48),.5+.5*q.y);
  col+=c0*g1*1.15+vio*g2*1.05+mint*g3*.75+blue*g1*g2*.6;
  float sw=sin(5.*atan(q.y,q.x)+d2*7.-tt*1.8)*.5+.5;
  col+=vec3(.25,.35,.6)*sw*.10*(1.-d2);
  col*=.62+.58*z;
  vec2 h=q-vec2(-.34,.44);col+=vec3(1.)*exp(-14.*dot(h,h))*.46;
  vec2 h2=q-vec2(.30,-.46);col+=mint*exp(-18.*dot(h2,h2))*.18;
  col+=mix(teal,vio,.5+.5*sin(tt*.8))*pow(1.-z,2.4)*.75;
  col=min(col,vec3(1.));
  float inside=smoothstep(R+.014,R-.014,r);
  float d=max(0.,r-R);
  float glow=exp(-8.*d)*(.34+.34*b);
  float ph=fract(t*(.42+b*.75));
  float ring=b*smoothstep(.03,0.,abs(d-ph*.2))*(1.-ph);
  vec3 gc=mix(teal,vio,.5+.5*sin(t*.9));
  float a=inside+(1.-inside)*clamp(glow+ring*.95,0.,1.);
  vec3 o=mix(gc,col,inside);
  gl_FragColor=vec4(o*a,a);
}`;

function orbGL(): boolean {
  if (ORB.ok !== null) return ORB.ok === true;
  try {
    const cv = document.createElement("canvas");
    cv.width = cv.height = 128;
    const gl = cv.getContext("webgl", { premultipliedAlpha: true, preserveDrawingBuffer: true, antialias: true });
    if (!gl) return (ORB.ok = false);
    const sh = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    };
    const pr = gl.createProgram()!;
    gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(pr);
    Object.assign(ORB, { cv, gl, pr, ext: gl.getExtension("KHR_parallel_shader_compile"), ok: "wait" });
    return orbReady();
  } catch {
    return (ORB.ok = false);
  }
}

function orbReady(): boolean {
  if (ORB.ok !== "wait") return ORB.ok === true;
  const { gl, pr, ext } = ORB;
  if (!gl || !pr) return (ORB.ok = false);
  try {
    if (ext && !gl.getProgramParameter(pr, ext.COMPLETION_STATUS_KHR)) return false;
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) return (ORB.ok = false);
    gl.useProgram(pr);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const ap = gl.getAttribLocation(pr, "p");
    gl.enableVertexAttribArray(ap);
    gl.vertexAttribPointer(ap, 2, gl.FLOAT, false, 0, 0);
    ORB.loc = { t: gl.getUniformLocation(pr, "t"), b: gl.getUniformLocation(pr, "b"), lo: gl.getUniformLocation(pr, "lo") };
    ORB.ok = true;
    return true;
  } catch {
    return (ORB.ok = false);
  }
}

/** No WebGL (or not compiled yet): the same sphere from radial gradients (prototype orbDraw2D). */
function draw2D(it: Item, t: number) {
  const { ctx, px } = it;
  const r = px / 2;
  const R = r * 0.78 * (1 + 0.025 * Math.sin(t * 1.5));
  const a = t * (0.38 + it.b * 1.25);
  ctx.clearRect(0, 0, px, px);
  const halo = ctx.createRadialGradient(r, r, R * 0.85, r, r, r);
  halo.addColorStop(0, `rgba(13,148,136,${0.32 + 0.3 * it.b})`);
  halo.addColorStop(1, "rgba(109,40,217,0)");
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, px, px);
  const g = ctx.createRadialGradient(r + Math.cos(a) * R * 0.35, r + Math.sin(a * 0.8) * R * 0.35, R * 0.1, r, r, R);
  g.addColorStop(0, "#5EEAD4");
  g.addColorStop(0.5, it.lo ? "#E11D48" : "#0D9488");
  g.addColorStop(1, "#6D28D9");
  ctx.beginPath();
  ctx.arc(r, r, R, 0, Math.PI * 2);
  ctx.fillStyle = g;
  ctx.fill();
  const hx = r - R * 0.35;
  const hy = r - R * 0.42;
  const hl = ctx.createRadialGradient(hx, hy, 0, hx, hy, R * 0.65);
  hl.addColorStop(0, "rgba(255,255,255,.6)");
  hl.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = hl;
  ctx.fill();
}

/*
 * The shader's pictures start in the first quiet moment after the console opens. A picture from it is copied off the
 * GPU into the orb's small canvas, and the first copy, made as the console's first page was being drawn, waited for
 * the GPU to finish that: 0.24 s in one go in the opening second (10/27 trace; 0.26 s on production, 10/27 morning).
 * Until then the orb is the same sphere from gradients.
 */
let calm = false;
let calmAsked = false;
function calmLater() {
  if (calmAsked) return;
  calmAsked = true;
  const w = window as Window & { requestIdleCallback?: (f: () => void, o?: { timeout: number }) => number };
  const settle = () => requestAnimationFrame(() => window.setTimeout(() => (calm = true), 0));
  window.setTimeout(() => (w.requestIdleCallback ? w.requestIdleCallback(settle, { timeout: 4000 }) : settle()), 1500);
}

/** One orb's picture at time t. */
function paint(it: Item, t: number) {
  if (calm && orbReady() && ORB.gl && ORB.cv && ORB.loc) {
    const { gl, cv, loc } = ORB;
    const px = it.px;
    // Grown to the biggest orb, never shrunk: a canvas resized is a new drawing buffer.
    if (cv.width < px) cv.width = cv.height = px;
    gl.viewport(0, 0, px, px);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform1f(loc.t, t + it.seed);
    gl.uniform1f(loc.b, it.b);
    gl.uniform1f(loc.lo, it.lo);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    it.ctx.clearRect(0, 0, px, px);
    // GL draws from the bottom left; that corner of the canvas is this orb.
    it.ctx.drawImage(cv, 0, cv.height - px, px, px, 0, 0, px, px);
  } else draw2D(it, t + it.seed);
}

function tick(now: number) {
  ORB.raf = 0;
  const t = (now - ORB.t0) / 1000;
  const still = stillWanted();
  let any = false;
  for (const it of ORB.items) {
    if (!it.vis) continue;
    any = true;
    it.b += ((it.busy || now < ORB.busyUntil ? 1 : 0) - it.b) * (still ? 1 : 0.06);
    paint(it, t);
  }
  if (any && !still && !document.hidden) ORB.raf = requestAnimationFrame(tick);
}

const wake = () => {
  if (!ORB.raf) ORB.raf = requestAnimationFrame(tick);
};

/** A new customer wrote, or 小爱 is answering: every orb on the page runs faster for a moment (prototype orbPulse). */
export function orbPulse(ms = 2600) {
  if (stillWanted()) return;
  ORB.busyUntil = performance.now() + ms;
  wake();
}

let listening = false;

/** tone "bad": rose at the heart, as the prototype's sad orb (she cannot answer, or everything is switched off). */
export default function AiOrb({ size = 22, tone = "", busy = false }: { size?: number; tone?: "" | "warn" | "bad"; busy?: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const item = useRef<Item | null>(null);
  const lo = tone === "bad" ? 1 : 0;
  useEffect(() => {
    const cv = ref.current;
    const ctx = cv?.getContext("2d");
    if (!cv || !ctx) return;
    if (!listening) {
      listening = true;
      document.addEventListener("visibilitychange", () => !document.hidden && wake());
    }
    const px = Math.round(size * Math.min(2, window.devicePixelRatio || 1));
    cv.width = cv.height = px;
    orbGL();
    calmLater();
    // Off screen, or on a page hidden by content-visibility, it is not drawn. Its first picture waits for the
    // observer, which reports after the page's layout: a new canvas's first drawImage lays the whole page out on the
    // spot, and from the next frame's start it did — 0.1–0.3 s as the console opened, or 小爱 did (10/27).
    const io =
      "IntersectionObserver" in window
        ? new IntersectionObserver(([e]) => {
            const was = it.vis;
            it.vis = !!e?.isIntersecting;
            if (!it.vis) return;
            if (!was) paint(it, (performance.now() - ORB.t0) / 1000);
            wake();
          })
        : null;
    const it: Item = { ctx, px, b: busy ? 1 : 0, busy, lo, seed: Math.random() * 40, vis: !io };
    item.current = it;
    ORB.items.add(it);
    io?.observe(cv);
    wake();
    return () => {
      io?.disconnect();
      ORB.items.delete(it);
      item.current = null;
    };
    // busy and lo are read here only as the first frame's; the effect below follows them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);
  useEffect(() => {
    const it = item.current;
    if (!it) return;
    it.busy = busy;
    it.lo = lo;
    wake();
  }, [busy, lo]);
  return (
    <span className={`ai-orb${busy ? " busy" : ""}`} style={{ width: size, height: size }} role="img" aria-label="小爱">
      <canvas ref={ref} style={{ width: size, height: size }} />
    </span>
  );
}
